import { localizedMessageOf } from "@/lib/localized-error";
import { useIntl } from "react-intl";
import { actingAsDeclaration } from "@/lib/share-item";
import { projectWriteError } from "@/lib/project-write-error";
import { useMemo } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { throwIfValidationRejected, useValidationFallback } from "@/components/match/use-match-editor";
import { ProjectDocument, UpdateProjectDocument } from "@/graphql/generated/operations";
import type { SettingsProps } from "./dashboard-kinds";
import { axesToRole, roleToAxes } from "./project-access";
import { parseStatus } from "./project-plan";
import { ProjectSettingsModal } from "./ProjectSettingsModal";

// Das Zahnrad eines Projekts der Art PROJECT: Name, Status und Zugriff als Rollen (Verwalten, Sehen).
// Die Zugriffszeilen liest es aus derselben Abfrage wie das Dashboard (aus dem Cache, ohne zweite
// Anfrage), denn die Rollen gibt es nur bei dieser Art.
export function ProjectDashboardSettings({ ctx, onSaveBasics, onClose }: SettingsProps) {
  const intl = useIntl();
  const validationFallback = useValidationFallback();
  const { data } = useQuery(ProjectDocument, { variables: { id: ctx.entity.id } });
  const [updateProject] = useMutation(UpdateProjectDocument);
  const project = data?.project ?? null;
  const owner = ctx.entity.company ? null : ctx.entity.createdBy;
  const status = parseStatus(ctx.data.status);
  // Bei persönlichen Projekten bleibt die Eigentümerin außerhalb der bearbeitbaren Zugriffsliste.
  // Bei Unternehmensprojekten ist der Ersteller ein gewöhnliches Mitglied.
  const access = useMemo(
    () =>
      (project?.access ?? []).flatMap((entry) =>
        entry?.person && entry.person.id !== owner?.id
          ? [
              {
                person: {
                  id: entry.person.id,
                  name: entry.person.formattedName ?? "",
                  avatarContent: entry.person.avatar?.content,
                  avatarMime: entry.person.avatar?.mimeType,
                },
                role: axesToRole({ plan: entry.plan, candidates: entry.candidates, timeline: entry.timeline }),
              },
            ]
          : [],
      ),
    [owner?.id, project],
  );

  return (
    <ProjectSettingsModal
      title={ctx.title}
      description={ctx.description}
      status={status}
      access={access}
      owner={owner}
      canEdit={ctx.entity.actions.canManageAccess}
      onSave={async (next) => {
        // Genesis ersetzt die Zugriffsliste und schützt bei persönlichen Projekten die Eigentümerin.
        try {
          await onSaveBasics({
            title: next.title,
            description: next.description,
            ...(next.status !== status ? { data: { status: next.status } } : {}),
          });
          const res = await updateProject({
            variables: {
              id: ctx.entity.id,
              input: {
                ...actingAsDeclaration(ctx.source.actingAsCompanyId ?? null),
                data: { access: next.access.map((row) => ({ person: { id: row.person.id }, ...roleToAxes(row.role) })) },
              },
            },
          });
          throwIfValidationRejected(res.data?.updateProject, "ValidateProjectResult", validationFallback);
        } catch (error) {
          if (localizedMessageOf(error)) throw error;
          throw projectWriteError(intl, error, ctx.source.actingAsCompanyId ?? null);
        }
      }}
      onClose={onClose}
    />
  );
}
